import { useCallback, useEffect, useRef, useState } from "react";

const OPENAI_TTS_URL = process.env.REACT_APP_OPENAI_TTS_URL || "https://techtok.me/api/openai-tts.php";

/**
 * "Free voice" = OpenAI TTS (not browser speech).
 * Keeps the same hook name to avoid refactors.
 */
export function useBrowserTTS({ onSpeakChange, onBoundary }) {
  const audioRef = useRef(null);
  const lastUrlRef = useRef(null);
  const lastTextRef = useRef("");

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [debug, setDebug] = useState("");

  const cleanupUrl = useCallback(() => {
    if (lastUrlRef.current) {
      try {
        URL.revokeObjectURL(lastUrlRef.current);
      } catch {}
      lastUrlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try {
        a.pause();
        a.currentTime = 0;
      } catch {}
    }
    setIsPlaying(false);
    setProgress(0);
    onSpeakChange?.(false);
  }, [onSpeakChange]);

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try {
        a.pause();
      } catch {}
    }
    setIsPlaying(false);
    onSpeakChange?.(false);
  }, [onSpeakChange]);

  const resume = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      await a.play();
      setIsPlaying(true);
      onSpeakChange?.(true);
    } catch (e) {
      setIsPlaying(false);
      onSpeakChange?.(false);
      setDebug("Audio blocked. Klik op Play.");
      console.warn("[OpenAI TTS] play blocked:", e);
    }
  }, [onSpeakChange]);

  const attachAudioEvents = useCallback(
    (audio, text) => {
      audio.ontimeupdate = () => {
        if (!audio.duration) return;
        const p = audio.currentTime / audio.duration;
        const clamped = Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : 0;
        setProgress(clamped);
        const charIndex = Math.floor(clamped * (text?.length || 0));
        onBoundary?.(charIndex);
      };

      audio.onplay = () => {
        setIsPlaying(true);
        onSpeakChange?.(true);
      };

      audio.onpause = () => {
        setIsPlaying(false);
        onSpeakChange?.(false);
      };

      audio.onended = () => {
        setIsPlaying(false);
        setProgress(1);
        onSpeakChange?.(false);
        setDebug("Audio ended ✅");
      };

      audio.onerror = (e) => {
        setIsPlaying(false);
        setProgress(0);
        onSpeakChange?.(false);
        setDebug("Audio playback error ❌");
        console.error("[OpenAI TTS] audio error:", e);
      };
    },
    [onBoundary, onSpeakChange]
  );

  const speak = useCallback(
    async (text) => {
      const t = (text || "").trim();
      if (!t) return;

      lastTextRef.current = t;
      stop();

      setDebug(`[OpenAI TTS] POST ${OPENAI_TTS_URL} (${t.length} chars)`);

      try {
        const res = await fetch(OPENAI_TTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: t,
            model: "gpt-4o-mini-tts",
            voice: "marin",
            response_format: "mp3",
            speed: 1.0,
          }),
        });

        const ct = res.headers.get("content-type") || "";
        if (!res.ok) {
          const errText = await res.text();
          setDebug(`[OpenAI TTS] ${res.status}: ${errText.slice(0, 220)}`);
          console.error("[OpenAI TTS] error:", res.status, errText);
          onSpeakChange?.(false);
          return;
        }

        if (!ct.includes("audio")) {
          const txt = await res.text();
          setDebug(`[OpenAI TTS] Unexpected content-type: ${ct}. Body: ${txt.slice(0, 220)}`);
          console.warn("[OpenAI TTS] unexpected response:", ct, txt);
          onSpeakChange?.(false);
          return;
        }

        const blob = await res.blob();
        cleanupUrl();
        const url = URL.createObjectURL(blob);
        lastUrlRef.current = url;

        const audio = new Audio(url);
        audioRef.current = audio;

        setProgress(0);
        onBoundary?.(0);
        attachAudioEvents(audio, t);

        try {
          await audio.play();
          setDebug("[OpenAI TTS] Playing ▶️");
        } catch (e) {
          setIsPlaying(false);
          onSpeakChange?.(false);
          setDebug("[OpenAI TTS] Audio blocked. Klik op Play.");
          console.warn("[OpenAI TTS] play blocked:", e);
        }
      } catch (e) {
        setDebug(`[OpenAI TTS] Fetch failed: ${String(e)}`);
        console.error("[OpenAI TTS] fetch failed:", e);
        onSpeakChange?.(false);
      }
    },
    [OPENAI_TTS_URL, attachAudioEvents, cleanupUrl, onBoundary, onSpeakChange, stop]
  );

  const replay = useCallback(async () => {
    const a = audioRef.current;
    if (a) {
      try {
        a.currentTime = 0;
      } catch {}
      await resume();
      return;
    }
    if (!lastTextRef.current) return;
    await speak(lastTextRef.current);
  }, [resume, speak]);

  useEffect(() => {
    return () => {
      try {
        audioRef.current?.pause?.();
      } catch {}
      cleanupUrl();
    };
  }, [cleanupUrl]);

  return {
    speak,
    replay,
    stop,
    pause,
    resume,
    isPlaying,
    progress,
    hasLastAudio: !!lastUrlRef.current || !!lastTextRef.current,
    debug,
  };
}
